import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/website-and-seo-services-colorado");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/website-and-seo-services-colorado")({
  head: () => ({
    meta: [
      { title: "Website and SEO Services: Why They Work Better Together (2026)" },
      { name: "description", content: "Buying website and SEO services separately usually means paying twice. Why the build and the rankings belong in one plan, what bundled services cost in Colorado, and what to ask." },
      { property: "og:title", content: "Website and SEO Services: Why They Work Better Together (2026)" },
      { property: "og:description", content: "Why your website build and your SEO belong in one plan — and what bundled services cost in Colorado." },
      { property: "og:url", content: `${SITE_URL}/learn/website-and-seo-services-colorado` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Website and SEO Services",
        description: ARTICLE?.desc ?? "Why website and SEO services work better together.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/website-and-seo-services-colorado" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="SEO"
      read="9 min read"
      title="Website and SEO services: why they work better together"
      intro="A lot of Colorado businesses buy a website from one company and SEO from another, then wonder why neither takes responsibility when the phone doesn't ring. The build and the rankings are the same job. Here's why — and what combined services should cost."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Website and SEO services" },
      ]}
      path="/learn/website-and-seo-services-colorado"
      faqs={[
        { q: "Can I buy a website first and add SEO later?", a: "You can, but you'll pay for the same work twice. SEO decisions — page structure, speed, service-by-city pages, schema — are baked in at build time. Retrofitting them costs more than building them in. Our Pro and Expert website packages include foundation SEO for exactly this reason." },
        { q: "What do website and SEO services cost together in Colorado?", a: "A site with foundation SEO runs $2,750 (10 pages) to $5,950 (up to 50 pages). Ongoing SEO runs $950–$2,750/month depending on how many cities and services you target. All prices are published on our pricing page." },
        { q: "My designer says the site is 'SEO-friendly.' Is that enough?", a: "Usually not. 'SEO-friendly' often means the site merely allows indexing. Real SEO is keyword research, pages built for each service and city, a complete Google Business Profile, and reviews — an ongoing effort, not a checkbox at launch." },
      ]}
    >
      <h2>The finger-pointing problem</h2>
      <p>
        When the website company and the SEO company are different vendors, every problem is the
        other guy's fault. The SEO says the site is too slow to rank. The designer says the SEO
        is making excuses. Meanwhile you pay both. One accountable team kills that dynamic: if
        the site doesn't rank and the phone doesn't ring, there's exactly one phone number to
        call.
      </p>

      <h2>SEO decisions happen at build time</h2>
      <p>
        Most of what makes a site rank is decided before the first page is designed:
      </p>
      <ul>
        <li><strong>Site structure.</strong> A page for each service, in each city you serve — that's architecture, not an add-on.</li>
        <li><strong>Speed.</strong> How the site is built determines how fast it loads. Google measures it; customers feel it.</li>
        <li><strong>Schema.</strong> The behind-the-scenes labels that tell Google and AI tools your services, locations, hours, and reviews.</li>
        <li><strong>Content plan.</strong> Which searches each page targets, so pages don't compete with each other.</li>
      </ul>
      <p>
        Retrofitting these onto a finished site means rebuilding it. Our{" "}
        <Link to="/learn/web-design-in-colorado">Colorado web design buyer's guide</Link> covers
        what a build should include from day one.
      </p>

      <h2>What combined services cost in Colorado</h2>
      <p>
        The build: <strong>$1,850</strong> (4 pages, SEO-ready), <strong>$2,750</strong> (10
        pages, foundation SEO done for you), or <strong>$5,950</strong> (up to 50 pages built to
        rank service-by-city). The ongoing work: <strong>$950/month</strong> for local SEO and
        Google Maps, <strong>$1,650/month</strong> adding AI search visibility and social,{" "}
        <strong>$2,750/month</strong> for multi-city campaigns with ads and reputation
        management. Everything is published on our <Link to="/pricing">pricing page</Link> —
        month to month, no long contracts.
      </p>

      <h2>What to do next</h2>
      <p>
        Start with a <Link to="/free-audit">free digital marketing audit</Link> — we'll look at
        your current site and rankings together and tell you honestly what needs fixing first, or
        see how we handle <Link to="/colorado-seo">SEO across Colorado</Link>.
      </p>
    </ArticleLayout>
  );
}
